import { tenants } from "./authSchema.mjs";
import { relations, sql } from "drizzle-orm";
import { boolean, index, integer, jsonb, pgTable, primaryKey, text, timestamp, uniqueIndex, uuid } from "drizzle-orm/pg-core";

//#region src/schema/globalSchema.ts
const locations = pgTable("locations", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	address: text("address").notNull(),
	city: text("city"),
	state: text("state"),
	zip: text("zip"),
	latitude: text("latitude"),
	longitude: text("longitude"),
	phoneNumber: text("phone_number"),
	faxNumber: text("fax_number"),
	organizationId: uuid("organization_id").references(() => organizations.id),
	source: text("source"),
	businessHours: jsonb("business_hours").$type(),
	facilityExternalId: text("facility_external_id"),
	addressKey: text("address_key"),
	phoneConfirmedAt: timestamp("phone_confirmed_at"),
	faxConfirmedAt: timestamp("fax_confirmed_at"),
	inactiveAt: timestamp("inactive_at"),
	createdAt: timestamp("created_at").defaultNow(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date())
}, (t) => [
	index("idx_locations_organization_id").on(t.organizationId),
	uniqueIndex("locations_facility_external_id_organization_id_unique").on(t.facilityExternalId, t.organizationId),
	index("idx_locations_state_city").on(t.state, t.city),
	index("idx_locations_zip").on(t.zip),
	index("idx_locations_address_key").on(t.addressKey)
]);
const organizations = pgTable("organizations", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	npi: text("npi").unique(),
	companyName: text("company_name"),
	aliases: text("aliases"),
	type: text("type"),
	offersTelehealth: boolean("offers_telehealth").default(false),
	isCca: boolean("is_cca"),
	qualityTier: integer("quality_tier"),
	ownershipType: text("ownership_type"),
	source: text("source"),
	website: text("website"),
	insuranceGroupsAccepted: jsonb("insurance_groups_accepted").$type(),
	insuranceGroupsNotAccepted: jsonb("insurance_groups_not_accepted").$type(),
	providerNpiForReferralAuth: text("provider_npi_for_referral_auth"),
	availability: text("availability"),
	availabilityUpdatedAt: timestamp("availability_updated_at"),
	specialtyDetails: text("specialty_details"),
	specialtyDetailsUpdatedAt: timestamp("specialty_details_updated_at"),
	externalId: text("external_id").unique(),
	healthSystemId: uuid("health_system_id").references(() => healthSystems.id),
	inactiveAt: timestamp("inactive_at"),
	createdAt: timestamp("created_at").defaultNow(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date())
}, (t) => [
	index("idx_organizations_health_system_id").on(t.healthSystemId),
	index("idx_organizations_company_name_trgm").using("gin", sql`lower(${t.companyName}) gin_trgm_ops`),
	index("idx_organizations_aliases_trgm").using("gin", sql`lower(${t.aliases}) gin_trgm_ops`)
]);
const providers = pgTable("providers", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	npi: text("npi").unique(),
	providerName: text("provider_name").notNull(),
	offersTelehealth: boolean("offers_telehealth").default(false),
	taxId: text("tax_id"),
	emailAddress: text("email_address"),
	age: integer("age"),
	gradYear: integer("grad_year"),
	gender: text("gender"),
	source: text("source"),
	externalId: text("external_id").unique(),
	inactiveAt: timestamp("inactive_at"),
	createdAt: timestamp("created_at").defaultNow(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()),
	tenantId: text("tenant_id").references(() => tenants.id)
}, (t) => [index("idx_providers_provider_name_trgm").using("gin", sql`lower(${t.providerName}) gin_trgm_ops`), index("idx_providers_tenant_id").on(t.tenantId)]);
const providerNpis = pgTable("provider_npis", {
	npi: text("npi").primaryKey(),
	providerId: uuid("provider_id").notNull().references(() => providers.id),
	createdAt: timestamp("created_at").defaultNow().notNull()
}, (t) => [index("idx_provider_npis_provider_id").on(t.providerId)]);
const organizationNpis = pgTable("organization_npis", {
	npi: text("npi").primaryKey(),
	organizationId: uuid("organization_id").notNull().references(() => organizations.id),
	createdAt: timestamp("created_at").defaultNow().notNull()
}, (t) => [index("idx_organization_npis_organization_id").on(t.organizationId)]);
/** A health system that owns practices, from Redshift's `seed_health_systems`. */
const healthSystems = pgTable("health_systems", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	externalId: text("external_id").notNull().unique(),
	name: text("name").notNull(),
	inactiveAt: timestamp("inactive_at"),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
});
/**
* One row per directory load, inserted first in the load's transaction, so a
* repeat load fails at once on the unique index. `loaded_at` is that
* transaction's now(), which every row the load inserts shares as its
* `created_at`, so a rollback finds them by timestamp.
*/
const directoryLoads = pgTable("directory_loads", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	state: text("state").notNull(),
	generationCreatedAt: timestamp("generation_created_at").notNull(),
	dataYear: integer("data_year").notNull(),
	candidateLogicId: text("candidate_logic_id").notNull(),
	loadedAt: timestamp("loaded_at").defaultNow().notNull(),
	rolledBackAt: timestamp("rolled_back_at"),
	counts: jsonb("counts").$type()
}, (t) => [uniqueIndex("directory_loads_state_generation_unique").on(t.state, t.generationCreatedAt).where(sql`rolled_back_at IS NULL`)]);
/**
* A kept row's columns as they were before a load overwrote or filled them,
* so a rollback reverts values as well as rows.
*/
const directoryLoadPriorValues = pgTable("directory_load_prior_values", {
	loadId: uuid("load_id").notNull().references(() => directoryLoads.id),
	tableName: text("table_name", { enum: [
		"providers",
		"organizations",
		"locations",
		"health_systems"
	] }).notNull(),
	rowId: uuid("row_id").notNull(),
	oldValues: jsonb("old_values").$type().notNull()
}, (t) => [primaryKey({ columns: [
	t.loadId,
	t.tableName,
	t.rowId
] })]);
const insurance = pgTable("insurance", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	planName: text("plan_name"),
	planType: text("plan_type"),
	payorId: text("payor_id"),
	payor: text("payor"),
	payorAbbreviation: text("payor_abbreviation"),
	payorPhoneNumber: text("payor_phone_number"),
	payorAddress: text("payor_address"),
	administrator: text("administrator"),
	specialistRefsReqPriorAuth: boolean("specialist_refs_req_prior_auth"),
	source: text("source"),
	createdAt: timestamp("created_at").defaultNow(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date())
});
const services = pgTable("services", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	procedureCode: text("procedure_code").notNull().unique(),
	procedureName: text("procedure_name").notNull(),
	createdAt: timestamp("created_at").defaultNow(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date())
});
/**
* Whether a `specialties` row is part of the clinical referral list, and at
* what level. See specs/specialty-taxonomy.md.
*/
let SpecialtyKind = /* @__PURE__ */ function(SpecialtyKind) {
	SpecialtyKind["Specialty"] = "specialty";
	/** Narrower than its parent, and detectable from credentials. */
	SpecialtyKind["Subspecialty"] = "subspecialty";
	/** Narrower than its parent, a practice interest with no credential. */
	SpecialtyKind["Focus"] = "focus";
	/** An existing or source name, pointing at a node through `maps_to_id`. */
	SpecialtyKind["Label"] = "label";
	return SpecialtyKind;
}({});
const specialties = pgTable("specialties", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	name: text("name").notNull(),
	aliases: text("aliases"),
	source: text("source"),
	kind: text("kind", { enum: [
		SpecialtyKind.Specialty,
		SpecialtyKind.Subspecialty,
		SpecialtyKind.Focus,
		SpecialtyKind.Label
	] }).notNull().default(SpecialtyKind.Label),
	parentId: uuid("parent_id").references(() => specialties.id),
	mapsToId: uuid("maps_to_id").references(() => specialties.id),
	createdAt: timestamp("created_at").defaultNow(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date())
}, (t) => [
	index("idx_specialties_parent_id").on(t.parentId),
	index("idx_specialties_maps_to_id").on(t.mapsToId),
	uniqueIndex("specialties_node_name_unique").on(sql`lower(${t.name})`).where(sql`kind <> 'label'`)
]);
const medicareFacilities = pgTable("medicare_facilities", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	cmsId: text("cms_id"),
	facilityName: text("facility_name"),
	address: text("address"),
	city: text("city"),
	state: text("state"),
	zip: text("zip"),
	phoneNumber: text("phone_number"),
	parentOrganization: text("parent_organization"),
	facilityType: text("facility_type"),
	facilitySubType: text("facility_sub_type"),
	hasEmergencyServices: boolean("has_emergency_services"),
	createdAt: timestamp("created_at").defaultNow(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date())
});
const providerOrganizations = pgTable("provider_organizations", {
	providerId: uuid("provider_id").notNull().references(() => providers.id),
	organizationId: uuid("organization_id").notNull().references(() => organizations.id),
	availability: text("availability"),
	availabilityUpdatedAt: timestamp("availability_updated_at"),
	specialtyDetails: text("specialty_details"),
	specialtyDetailsUpdatedAt: timestamp("specialty_details_updated_at"),
	createdAt: timestamp("created_at").defaultNow()
}, (t) => [primaryKey({ columns: [t.providerId, t.organizationId] }), index("idx_provider_organizations_organization_id").on(t.organizationId)]);
const providerSpecialties = pgTable("provider_specialties", {
	providerId: uuid("provider_id").notNull().references(() => providers.id),
	specialtyId: uuid("specialty_id").notNull().references(() => specialties.id),
	source: text("source"),
	createdAt: timestamp("created_at").defaultNow()
}, (t) => [primaryKey({ columns: [t.providerId, t.specialtyId] }), index("idx_provider_specialties_specialty_id").on(t.specialtyId)]);
const providerLocations = pgTable("provider_locations", {
	providerId: uuid("provider_id").notNull().references(() => providers.id),
	locationId: uuid("location_id").notNull().references(() => locations.id),
	createdAt: timestamp("created_at").defaultNow()
}, (t) => [primaryKey({ columns: [t.providerId, t.locationId] }), index("idx_provider_locations_location_id").on(t.locationId)]);
const organizationsRelations = relations(organizations, ({ many }) => ({
	locations: many(locations),
	providerOrganizations: many(providerOrganizations)
}));
const locationsRelations = relations(locations, ({ one, many }) => ({
	organization: one(organizations, {
		fields: [locations.organizationId],
		references: [organizations.id]
	}),
	providerLocations: many(providerLocations)
}));
const providersRelations = relations(providers, ({ one, many }) => ({
	tenant: one(tenants, {
		fields: [providers.tenantId],
		references: [tenants.id]
	}),
	providerOrganizations: many(providerOrganizations),
	providerSpecialties: many(providerSpecialties),
	providerLocations: many(providerLocations)
}));
const specialtiesRelations = relations(specialties, ({ many }) => ({ providerSpecialties: many(providerSpecialties) }));
const providerOrganizationsRelations = relations(providerOrganizations, ({ one }) => ({
	provider: one(providers, {
		fields: [providerOrganizations.providerId],
		references: [providers.id]
	}),
	organization: one(organizations, {
		fields: [providerOrganizations.organizationId],
		references: [organizations.id]
	})
}));
const providerSpecialtiesRelations = relations(providerSpecialties, ({ one }) => ({
	provider: one(providers, {
		fields: [providerSpecialties.providerId],
		references: [providers.id]
	}),
	specialty: one(specialties, {
		fields: [providerSpecialties.specialtyId],
		references: [specialties.id]
	})
}));
const providerLocationsRelations = relations(providerLocations, ({ one }) => ({
	provider: one(providers, {
		fields: [providerLocations.providerId],
		references: [providers.id]
	}),
	location: one(locations, {
		fields: [providerLocations.locationId],
		references: [locations.id]
	})
}));

//#endregion
export { SpecialtyKind, directoryLoadPriorValues, directoryLoads, healthSystems, insurance, locations, locationsRelations, medicareFacilities, organizationNpis, organizations, organizationsRelations, providerLocations, providerLocationsRelations, providerNpis, providerOrganizations, providerOrganizationsRelations, providerSpecialties, providerSpecialtiesRelations, providers, providersRelations, services, specialties, specialtiesRelations };